import { safeLocalGet, safeLocalSet } from "@/lib/browser-storage";

// Client-side checkout prefill memory (browser-autofill / "remember me" pattern).
//
// WHY this and not the reading record: the chat captures the seeker's contact
// (phone/email) during the conversation, but the value surface /chat/[readingId]
// is a separate page load with no in-memory state. The industry standard is to
// prefill contact at checkout to cut abandonment (Razorpay/Stripe both recommend
// it) — but NEVER by exposing the PII on the shareable bearer-capability link
// (OWASP "information exposure through query strings"/capability-URL anti-pattern:
// a forwarded /chat link would leak the contact). For an anonymous, same-device
// flow with no login, the privacy-safe standard is client-side storage: the
// contact stays on the user's own device, never in a URL, page render, referrer,
// log, or API response. See lesson_razorpay_chat_prefill (empty prefill →
// void@razorpay.com).

const KEY = "ishvaram_checkout_prefill";

export type CheckoutPrefill = { email?: string; contact?: string };
export type CapturedCheckoutContact = { email?: string; phone?: string; contact?: string };

function clean(p: CheckoutPrefill): CheckoutPrefill {
  const out: CheckoutPrefill = {};
  if (typeof p.email === "string" && p.email.trim()) out.email = p.email.trim();
  if (typeof p.contact === "string" && p.contact.trim()) out.contact = p.contact.trim();
  return out;
}

/**
 * Merge-and-persist the captured contact. No-op if nothing usable is present.
 * @internal
 */
export function setCheckoutPrefill(next: CheckoutPrefill): void {
  const merged = clean({ ...getCheckoutPrefill(), ...next });
  if (!merged.email && !merged.contact) return;
  safeLocalSet(KEY, JSON.stringify(merged));
}

/** Remember any successful contact capture for the next report checkout on this device. */
export function rememberCheckoutPrefillFromContact(contact: CapturedCheckoutContact): void {
  setCheckoutPrefill({
    email: contact.email,
    contact: contact.contact ?? contact.phone,
  });
}

/** Read the stored prefill. Returns {} on server, missing, or malformed. */
export function getCheckoutPrefill(): CheckoutPrefill {
  const raw = safeLocalGet(KEY);
  if (!raw) return {};
  try {
    return clean(JSON.parse(raw) as CheckoutPrefill);
  } catch {
    return {};
  }
}
